/**
 * useIsMobile — viewport-driven boolean to decide whether to render the
 * mobile or desktop variant of a page.
 *
 * Why this hook exists
 * ────────────────────
 * Several pages (ResearchChat, Landing) were authored as two complete
 * sibling components — `<MobileX />` + `<DesktopX />` — both rendered
 * unconditionally into the DOM with Tailwind `hidden`/`lg:hidden` doing
 * the visual hide. That worked for layout but cost us:
 *
 *   • twice the React reconciliation work per state change
 *   • twice the useEffect runs (incl. setInterval, fetch on mount, …)
 *   • twice the DOM nodes (memory pressure on iOS Safari especially)
 *   • streaming chats actually rendered the same delta into both bubbles
 *
 * Switching to a viewport hook lets us render *only one* variant at a
 * time. Important properties we preserve:
 *
 *   • The Tailwind `lg:` breakpoint is 1024 px — we mirror that exact
 *     number here so the cutover behaves identically to the old CSS.
 *   • iOS Capacitor / native shell: viewport is always sub-1024 → returns
 *     `true` → MobileX renders. Matches today's behaviour exactly.
 *   • Re-evaluates on window resize / orientation change so a user
 *     dragging the browser between sizes still gets the right variant.
 *   • Returns `true` on the very first render when window is undefined
 *     (e.g. SSR — not used today, but cheap to be correct) to default to
 *     the smaller layout that won't overflow.
 */
import { useEffect, useState } from "react";

const BREAKPOINT_PX = 1024; // Tailwind `lg` — mirror this if you change theme.

export default function useIsMobile() {
  const get = () => {
    if (typeof window === "undefined") return true;
    return window.innerWidth < BREAKPOINT_PX;
  };
  const [mobile, setMobile] = useState(get);

  useEffect(() => {
    // We use matchMedia (cheaper than resize+innerWidth on every event)
    // when available, falling back to resize for ancient WebViews. Both
    // paths produce identical results — only the cost differs.
    const mql = window.matchMedia(`(max-width: ${BREAKPOINT_PX - 1}px)`);
    const handler = (e) => setMobile(e.matches);
    // Safari < 14 used addListener; modern is addEventListener.
    if (mql.addEventListener) mql.addEventListener("change", handler);
    else mql.addListener(handler);
    // Sync once in case the initial state was wrong (rare: rotation
    // happens between useState init and useEffect mount).
    setMobile(mql.matches);
    return () => {
      if (mql.removeEventListener) mql.removeEventListener("change", handler);
      else mql.removeListener(handler);
    };
  }, []);

  return mobile;
}
